import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Badge } from '../components/ui/badge';
import { 
    Music, 
    Calendar, 
    ArrowRight,
    Loader2,
    Send
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const statusLabels = {
    new: 'New',
    reviewing: 'Under Review',
    shortlisted: 'Shortlisted',
    revisions_requested: 'Revisions Requested',
    ready_to_submit: 'Ready to Submit',
    submitted: 'Submitted',
    placed: 'Placed',
    rejected: 'Rejected'
};

const statusClasses = {
    new: 'status-new',
    reviewing: 'status-reviewing',
    shortlisted: 'status-shortlisted',
    revisions_requested: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    ready_to_submit: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
    submitted: 'status-submitted',
    placed: 'status-placed',
    rejected: 'status-rejected'
};

export default function SubmissionsPage() {
    const { token } = useAuth();
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchSubmissions();
    }, []);

    const fetchSubmissions = async () => {
        try {
            const response = await fetch(`${API}/submissions`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setSubmissions(data);
            } else {
                toast.error('Failed to load submissions');
            }
        } catch (error) {
            toast.error('Error loading submissions');
        } finally {
            setLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    return (
        <Layout>
            <div className="max-w-5xl mx-auto" data-testid="submissions-page">
                {/* Header */}
                <div className="mb-8">
                    <motion.h1 
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-3xl lg:text-4xl font-bold text-white mb-2"
                    >
                        My Submissions
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-[#A3A3A3]"
                    >
                        Track the status of your submitted tracks
                    </motion.p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                    </div>
                ) : submissions.length === 0 ? (
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-col items-center justify-center py-20 text-center"
                    >
                        <div className="w-20 h-20 rounded-full bg-[#262626] flex items-center justify-center mb-4">
                            <Send size={36} className="text-[#A3A3A3]" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">No submissions yet</h3>
                        <p className="text-[#A3A3A3] mb-6 max-w-md">
                            Browse available sync opportunities and submit your tracks to get started.
                        </p>
                        <Link 
                            to="/opportunities"
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            Browse Opportunities
                            <ArrowRight size={18} />
                        </Link>
                    </motion.div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                        className="space-y-4"
                    >
                        {submissions.map((sub) => (
                            <motion.div key={sub.id} variants={itemVariants}>
                                <Link
                                    to={`/submissions/${sub.id}`}
                                    data-testid={`submission-${sub.id}`}
                                    className="block"
                                >
                                    <div className="card-interactive p-5 flex items-center gap-4">
                                        {/* Track icon */}
                                        <div className="w-14 h-14 rounded-xl bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
                                            <Music size={24} className="text-[#F51E1E]" />
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg font-bold text-white truncate mb-1">
                                                {sub.track_title || 'Untitled Track'}
                                            </h3>
                                            <p className="text-sm text-[#A3A3A3] truncate">
                                                {sub.sync_title || 'Unknown Opportunity'}
                                            </p>
                                        </div>

                                        {/* Status & Date */}
                                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                                            <Badge 
                                                variant="outline"
                                                className={statusClasses[sub.status] || 'status-new'}
                                            >
                                                {statusLabels[sub.status] || sub.status}
                                            </Badge>
                                            <div className="flex items-center gap-1 text-xs text-[#A3A3A3]">
                                                <Calendar size={12} />
                                                <span>
                                                    {new Date(sub.created_at).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Arrow */}
                                        <ArrowRight size={20} className="text-[#A3A3A3] flex-shrink-0" />
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>
        </Layout>
    );
}
